import React, { useRef, useEffect, useState } from "react";
import { connect } from "react-redux";
import FeaturePermissionTypeCodes from "typeConstants/featurePermissionCodes";
import { MSpinner, MCard, MGrid } from "components/Shared";
import { FILTER_TYPE_NONE } from "components/Shared/MGrid";
import { userHasRight } from "selectors/rights";
import { CUSTOMER_DETAILS_EDIT } from "typeConstants/rights";
import { getFeaturePermissionStates, updateFeaturePermissionStates } from "requests/featurePermission";
import { getStateAbbreviationList } from "typeConstants/states";
import { defaultMaxHeightStyle } from "styles/sharedStyles";

interface FeaturePermissionStatesProps {
  // component
  cellWidth: number;
  customerNumber: number;
  featurePermissionType: FeaturePermissionTypeCodes;

  // redux
  canEdit: boolean;
}

const FeaturePermissionStates = ({
  cellWidth,
  customerNumber,
  featurePermissionType,
  canEdit,
}: FeaturePermissionStatesProps) => {

  const mounted = useRef(true);
  const [data, setData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedData, setSelectedData] = useState([]);

  useEffect(() => {
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    setData(null);

    if(customerNumber && featurePermissionType) {
      getFeaturePermissionStates(customerNumber, featurePermissionType, setData, mounted);
    }
  }, [customerNumber, featurePermissionType]);

  // calc title + inclusive/exclusive
  const getTitle = () => {
    switch(featurePermissionType) {
      case FeaturePermissionTypeCodes.CALL_CENTER_INCLUSION:
        return "Scheduling Queue Allowed States";
      default:
        return "Unknown";
    }
  };

  const onEditStarted = () => {
    const selected = getStateAbbreviationList().reduce((acc, state, i) => {
      if(data.some(d => d === state))
        acc.push(i);

      return acc;
    }, []);

    setSelectedData(selected);
    setIsEditing(true);
  };

  const onChange = (selected) => {
    setSelectedData(selected);
  };

  const getFields = () => {
    if (!data) {
      return [
        { key: "loading", control: (<MSpinner/>) }
      ];
    }

    const columns = [ { key: "state", name: "State", dataSource: "state", filterType: FILTER_TYPE_NONE, } ];

    let gridData;
    if(isEditing) {
      gridData = getStateAbbreviationList().map(s => { return { state: s }; });      
    }
    else if (!data.length) {
      return [
        { key: "all-states", control: (<div>All States Allowed</div>) }
      ];
    }
    else {
      gridData = data.map(s => { return { state: s }; });
    }

    return [
      {
        key: "grid",
        control: (
          <MGrid 
            columnDefinitions={columns}
            data={gridData}
            {
              ...isEditing && {
                allowRowSelection: true,
                selectedRows: selectedData,
                onRowSelectionChanged: onChange,
              }
            }
          />
        )
      }
    ];
  };

  const saveChanges = () => {
    const saveData = selectedData.map(si => getStateAbbreviationList()[si]);
    setData(null);
    updateFeaturePermissionStates(customerNumber, featurePermissionType, saveData, 
      () => {
        getFeaturePermissionStates(customerNumber, featurePermissionType, setData, mounted);
      }, mounted);
  };

  return (
    <>
    <MCard
      className={"mcard-mgrid-175"}
      cellWidth={cellWidth}
      title={getTitle()}
      fields={getFields()}
      isEditable={canEdit}
      singleColumn
      onEditStarted={onEditStarted}
      onEditCancelled={() => setIsEditing(false)}
      onEditFinished={saveChanges}
      sx={defaultMaxHeightStyle}
    />    
    </>
  );
};

const mapStateToProps = state => ({
  canEdit: userHasRight(CUSTOMER_DETAILS_EDIT)(state),
});

export default connect(mapStateToProps)(FeaturePermissionStates);